import { camelCase, upperFirst } from "lodash-es";

import ModalService from "shared/modals/ModalService";
import type {
  RadioClip as RadioClipResource,
  TvCaption as TvCaptionResource,
} from "shared/resources";
import {
  fetchCurrentUserMediaRequests,
  isMediaRequestProcessed,
} from "shared/services/mediaRequest";
import { type Nullable } from "shared/types";
import type {
  MediaRequest,
  Mention,
  MentionType,
  PodcastEpisode,
  RadioClip,
  RadioClipFromResource,
  TvCaption,
  TvCaptionFromResource,
} from "shared/types/mentions";
import { isRadioClip, isTvCaption } from "shared/types/mentions/guards";

const MEDIA_REQUEST_POLLING_INTERVAL_MS = 10_000;
const MEDIA_REQUEST_POLLING_MAX_TIMES = 10;

function isTvEyesBroadcastMention(
  mention: Mention
): mention is TvCaption | RadioClip {
  return isTvCaption(mention) || isRadioClip(mention);
}

function isTvEyesContent(mention: Mention): boolean {
  return (
    isTvEyesBroadcastMention(mention) &&
    Boolean(mention.tveyes_player_url?.length)
  );
}

function hasArchivedTvEyesContent(mention: Mention): boolean {
  return (
    isTvEyesBroadcastMention(mention) &&
    Boolean(mention.tveyes_media_request?.status) &&
    mention.tveyes_media_request?.status !== "Cancelled"
  );
}

function openTvEyesPlayerModal(
  tveyesMention: TvCaption | RadioClip | PodcastEpisode,
  {
    forceFullBroadcast = false,
    mediaRequest,
  }: { forceFullBroadcast?: boolean; mediaRequest?: MediaRequest } = {}
) {
  ModalService.open("TveyesPlayerModal", {
    props: {
      mention: tveyesMention,
      forceFullBroadcast,
      mediaRequest,
    },
    events: {
      close: async () => {
        if (!mediaRequest && isTvEyesBroadcastMention(tveyesMention)) {
          await pollTvEyesMentionMediaRequest(tveyesMention);
        }
      },
    },
  });
}

async function fetchTveyesMentionMediaRequest(
  tveyesMention: TvCaption | RadioClip
): Promise<Nullable<MediaRequest>> {
  const query = {
    sourceId: tveyesMention.id,
    sourceType: upperFirst(camelCase(tveyesMention.type)),
    originKeyword: "TvEyes",
  };

  const mediaRequests = await fetchCurrentUserMediaRequests(query);

  return mediaRequests[0]; // with the conditions above it should be at most one
}

async function pollTvEyesMentionMediaRequest(
  tvEyesMention: TvCaption | RadioClip
) {
  const pollIfNeeded = async (pollsSoFar = 0) => {
    if (pollsSoFar < MEDIA_REQUEST_POLLING_MAX_TIMES) {
      const mediaRequest = await fetchTveyesMentionMediaRequest(tvEyesMention);

      // eslint-disable-next-line no-param-reassign
      tvEyesMention.tveyes_media_request = mediaRequest ?? undefined; // "?? undefined"  makes TS happy

      if (!mediaRequest) return; // no media request - nothing to do

      if (!isMediaRequestProcessed(mediaRequest)) {
        // not processed yet - keep polling
        setTimeout(
          () => pollIfNeeded(pollsSoFar + 1),
          MEDIA_REQUEST_POLLING_INTERVAL_MS
        );
      }
    }
  };

  await pollIfNeeded();
}

function mapTvCaptionFromResource(
  tvCaptionResource: TvCaptionResource
): TvCaptionFromResource {
  return {
    id: Number(tvCaptionResource.id),
    type: tvCaptionResource.type as MentionType,
    text: tvCaptionResource.text,
    start_time: tvCaptionResource.startTime,
    end_time: tvCaptionResource.endTime,
    program_exclusion: tvCaptionResource.programExclusion,
    source: ((sourceResource) => ({
      id: Number(sourceResource.id),
      location: sourceResource.location.name,
      logo_url: sourceResource.logoUrl,
      name: sourceResource.name,
      time_zone: sourceResource.timeZone,
    }))(tvCaptionResource.source),
  };
}

function mapRadioClipFromResource(
  radioClipResource: RadioClipResource
): RadioClipFromResource {
  return {
    id: Number(radioClipResource.id),
    type: radioClipResource.type as MentionType,
    text: radioClipResource.text,
    start_time: radioClipResource.startTime,
    end_time: radioClipResource.endTime,
    ucp_id: radioClipResource.ucpId,
    created_at: radioClipResource.createdAt,
    audience: radioClipResource.audience,
    language_code: radioClipResource.languageCode,
    source: ((sourceResource) => ({
      id: Number(sourceResource.id),
      location: sourceResource.location.name,
      logo_url: sourceResource.logoUrl,
      name: sourceResource.name,
      time_zone: sourceResource.timeZone,
    }))(radioClipResource.source),
  };
}

export {
  fetchTveyesMentionMediaRequest,
  hasArchivedTvEyesContent,
  isTvEyesBroadcastMention,
  isTvEyesContent,
  mapRadioClipFromResource,
  mapTvCaptionFromResource,
  openTvEyesPlayerModal,
};
